import React from 'react'
import { Box } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { CountryCode } from '@carsys/enums/country-code'
import isFunction from 'lodash/fp/isFunction'
import classNames from 'classnames'
import euStartImage from '@carsys/ui/src/assets/eu-stars.png'
import { LicensePlateVariant } from '@carsys/ui/src/LicensePlate/enum'
export { LicensePlateVariant } from '@carsys/ui/src/LicensePlate/enum'
import { useGetRegistrationDisplayConfig } from '@carsys/ui/src/LicensePlate/use-get-registration-display-config'

export interface Props {
  size?: 'small' | 'large' | 'big' | 'huge' | 'massive'
  required?: boolean
  disabled?: boolean
  country: string
  variant?: LicensePlateVariant /* styling variant. some countries have different variants. */
  overwriteIsSquare?: boolean /* will show as square/truck plate. generally handaled by variant, but can be overwritten */
  overwriteIsEU?: boolean /* will show a eu-stars in blue-box. generally set automatically via country */
  overwriteIsBlueBox?: boolean /* show a blue-box. generally set automatically via country/variant, but can be overwritten. */
  value: string
  children?: (classes: ReturnType<typeof useLicensePlateInputStyles>, rows: number, maskedValue: string) => JSX.Element
}

const licensePlateHeight = 30
const licensePlateMinWidth = 142
const licensePlateBorderWidth = 2
const licensePlateBorderWidthCountry = 1
const licensePlateBorderRadius = 5
const licensePlateBorderColor = '#0000004d'
const licensePlateBorderColorCountry = '#ffffff38'

const licensePlateCountryFontSize = 7
const licensePlateCountryPadding = 2
const licensePlateCountryMinWidth = 14
const licensePlateFontSize = 20

const squareHeightModifier = 2
const largeModifier = 1.3
const bigModifier = 1.75
const hugeModifier = 2
const massiveModifier = 3
const tinyHeight = 22
const tinyMinWidth = 60
const smallHeight = 24
const smallMinWidth = 100
const textfieldSelectors = `&> .number input,&> .number textarea`
const textfieldSelectorsAndNumber = `&> .number,${textfieldSelectors}` // classes are re-used, inner spans letters should also be styled

const sizeByModifier = (name: string, modifier: number) => {
  if (name === 'tiny' || name === 'small') {
    const isTiny = name === 'tiny'
    // for smaller sizes, don't mess with borders and paddings:
    return {
      [`&.${name}`]: {
        height: isTiny ? tinyHeight : smallHeight,
        minWidth: isTiny ? tinyMinWidth : smallMinWidth,
        '&.square': {
          height: (isTiny ? tinyHeight : smallHeight) * squareHeightModifier
        },
        '&> .country': {
          height: (isTiny ? tinyHeight : smallHeight) - (licensePlateCountryPadding * 2 - licensePlateBorderWidthCountry * 2),
          paddingTop: 0,
          paddingBottom: 0
        },
        '&> .number': {
          fontSize: `12px !important`,
          letterSpacing: `0px !important`
        },
        [textfieldSelectors]: {
          width: (isTiny ? tinyMinWidth : smallMinWidth) - (licensePlateCountryMinWidth + licensePlateBorderWidth * modifier),
          fontSize: `12px !important`,
          minWidth: 'auto !important'
        }
      }
    }
  }

  return {
    [`&.${name}`]: {
      height: licensePlateHeight * modifier,
      minWidth: licensePlateMinWidth * modifier,
      '&.square': {
        height: licensePlateHeight * squareHeightModifier * modifier,
        '&> .country': {
          borderRadius: `${licensePlateBorderRadius * modifier}px 0 0 0`
        }
      },
      '&> .country': {
        borderRadius: `${licensePlateBorderRadius * modifier}px 0 0 ${licensePlateBorderRadius * modifier}px`,
        borderWidth: `${licensePlateBorderWidth * modifier}px`,
        fontSize: `${licensePlateCountryFontSize * modifier}px`,
        padding: `${licensePlateCountryPadding * modifier}px`,
        minWidth: `${Math.ceil(licensePlateCountryMinWidth + (licensePlateBorderWidth * modifier) / 2)}px`,
        /* should not grow to full height, square plates don't have full height */
        height: Math.ceil(
          licensePlateHeight * modifier - licensePlateCountryPadding * modifier * 2 - licensePlateBorderWidth * modifier * 2
        )
      },
      '&> .number': {
        borderWidth: licensePlateBorderWidth * modifier,
        borderRadius: `${licensePlateBorderRadius * modifier}px`,
        marginLeft:
          -1 * Math.floor(licensePlateCountryMinWidth + licensePlateCountryPadding * 2 * modifier + licensePlateBorderWidth * modifier * 2),
        paddingLeft: Math.floor(
          licensePlateCountryMinWidth + licensePlateCountryPadding * 2 * modifier + licensePlateBorderWidth * modifier
        ),
        fontSize: `${licensePlateFontSize * modifier}px`
      },
      [textfieldSelectors]: {
        width:
          licensePlateMinWidth * modifier -
          (licensePlateCountryMinWidth * modifier + licensePlateBorderWidth * modifier * 2 + licensePlateCountryPadding * modifier * 2),
        fontSize: `${licensePlateFontSize * modifier}px`
      }
    }
  }
}

export const useLicensePlateInputStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  licenseplate: {
    display: 'inline-flex',
    boxSizing: 'border-box',
    height: licensePlateHeight,
    minWidth: licensePlateMinWidth,
    color: '#121212',
    fontFamily: 'sans-serif',
    '&.square': {
      height: licensePlateHeight * squareHeightModifier,
      '&> .country': {
        borderRadius: `${licensePlateBorderRadius}px 0 0 0`
      }
    },
    '&> .country': {
      display: 'flex',
      justifyContent: 'center',
      minWidth: `${licensePlateCountryMinWidth}px`,
      padding: `${licensePlateCountryPadding}px`,
      backgroundColor: '#003697',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center 3px',
      backgroundSize: 'contain',
      color: '#fff',
      zIndex: 1,
      boxSizing: 'content-box',
      height: licensePlateHeight - licensePlateCountryPadding * 2 - licensePlateBorderWidthCountry * 2,
      fontSize: `${licensePlateCountryFontSize}px`,
      lineHeight: `${licensePlateCountryFontSize}px`,
      border: `${licensePlateBorderWidthCountry}px solid ${licensePlateBorderColorCountry}`,
      borderRight: 'none',
      borderRadius: `${licensePlateBorderRadius}px 0 0 ${licensePlateBorderRadius}px`,
      textTransform: 'uppercase',
      '&> span:first-child': { alignSelf: 'flex-end' }
    },
    '&> .number': {
      boxSizing: 'inherit',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      whiteSpace: 'nowrap',
      flex: '1 1 auto',
      height: '100%',
      alignSelf: 'center',
      fontFamily: 'kentekenregular',
      fontSize: licensePlateFontSize,
      lineHeight: 'normal',
      textAlign: 'center',
      textTransform: 'uppercase !important',
      overflow: 'hidden',
      zIndex: 0,
      marginLeft: -1 * (licensePlateCountryMinWidth + licensePlateCountryPadding * 2 + licensePlateBorderWidthCountry),
      paddingLeft: 10 + licensePlateCountryMinWidth + licensePlateCountryPadding * 2 + licensePlateBorderWidthCountry,
      paddingRight: `10px`,
      border: `${licensePlateBorderWidth}px solid ${licensePlateBorderColor}`,
      borderRadius: `${licensePlateBorderRadius}px`,
      backgroundColor: `#fff`,
      [`& .disabled`]: { opacity: `1 !important` },
      [`&.field`]: { width: `100%` },
      [`&.stripe`]: { padding: `0 1px` },
      [`&.spacer`]: { flexGrow: 2 }
    },
    '&> .eu': {
      backgroundImage: `url('${euStartImage}')`
    },
    '&.without-box': {
      '&> .number': {
        paddingLeft: '10px',
        marginLeft: 0
      },
      '&> .no-blue-box': {
        border: 'none',
        backgroundColor: `#fff`,
        minWidth: 0,
        padding: 0,
        '&> span': {
          display: 'none'
        }
      }
    },
    [textfieldSelectors]: {
      padding: `0 !important`,
      border: `none !important`,
      background: `transparent !important`,
      fontFamily: `'kentekenregular' !important`,
      fontSize: `16px`,
      lineHeight: 1.25,
      textAlign: `center !important`,
      [`&:focus`]: { border: `none`, background: `transparent` }
    },

    ...sizeByModifier('tiny', 0.25),
    ...sizeByModifier('small', 5),
    ...sizeByModifier('medium', 0.85),
    ...sizeByModifier('large', largeModifier),
    ...sizeByModifier('big', bigModifier),
    ...sizeByModifier('huge', hugeModifier),
    ...sizeByModifier('massive', massiveModifier),

    /* countries */
    '&.neutral': {
      [textfieldSelectorsAndNumber]: {
        fontSize: '18px',
        paddingTop: '2px',
        letterSpacing: '1px',
        color: '#121212 !important'
      }
    },
    '&.BE': {
      '&> .number': {
        paddingTop: '1px',
        borderColor: '#9b111e'
      },
      [textfieldSelectorsAndNumber]: {
        color: '#9b111e !important',
        fontFamily: `'kentekenbe' !important`,
        fontSize: '24px'
      }
    },
    '&.CZ': {
      '&> .number': {
        paddingTop: '5px'
      },
      [textfieldSelectorsAndNumber]: {
        fontFamily: `'kentekencz' !important`,
        fontSize: '20px',
        color: '#121212 !important'
      }
    },
    '&.DE': {
      [textfieldSelectorsAndNumber]: {
        fontFamily: `'kentekende' !important`,
        fontSize: '23px',
        color: '#121212 !important'
      }
    },
    '&.FR': {
      '&> .number': {
        paddingTop: '2px'
      },
      [textfieldSelectorsAndNumber]: {
        fontFamily: `'kentekenfr' !important`,
        fontSize: '20px',
        letterSpacing: '1px',
        color: '#121212 !important'
      }
    },
    '&.IT': {
      [textfieldSelectorsAndNumber]: {
        fontFamily: `'kentekenit' !important`,
        fontSize: `21px`,
        letterSpacing: `1px`,
        color: `#121212 !important`
      }
    },
    '&.GB': {
      [textfieldSelectorsAndNumber]: {
        fontFamily: `'kentekenuk' !important`,
        fontSize: '25px',
        color: '#121212 !important'
      }
    },
    '&.NL': {
      '&> .number': {
        backgroundColor: '#ffcf00'
      },
      '&.variant-green': {
        '&> .number': {
          backgroundColor: '#088f73'
        }
      },
      '&.variant-blue': {
        '&> .number': {
          backgroundColor: '#4fa0de'
        }
      },
      '&.variant-white': {
        '&> .number': {
          backgroundColor: '#ffffff'
        }
      },
      [textfieldSelectorsAndNumber]: {
        fontSize: '18px',
        color: '#121212 !important'
      }
    }
  },
  input: {
    position: 'absolute !important' as any,
    zIndex: 20,
    width: 'auto',
    height: '100%',
    '&> *': {
      paddingTop: '0 !important',
      minWidth: '100px !important'
    }
  },
  hiddenSpacer: {
    position: 'relative',
    left: 0,
    opacity: 0,
    userSelect: 'none',
    zIndex: 1,
    width: 0,
    overflow: 'hidden'
  }
}))

export const LicensePlateInputBase = ({
  country,
  size,
  required,
  value,
  disabled,
  children,
  variant,
  overwriteIsEU,
  overwriteIsBlueBox,
  overwriteIsSquare
}: React.PropsWithChildren<Props>) => {
  const classes = useLicensePlateInputStyles()
  const registrationDisplay = useGetRegistrationDisplayConfig((country as CountryCode) || '', variant)
  const isEU = overwriteIsEU !== undefined ? overwriteIsEU : registrationDisplay.isEU
  const isBlueBox = overwriteIsBlueBox !== undefined ? overwriteIsBlueBox : registrationDisplay.isBlueBox
  const isSquare = overwriteIsSquare !== undefined ? overwriteIsSquare : registrationDisplay.isSquare
  const mappedCountry = registrationDisplay?.countryCode || country
  const baseClassNames = classNames({
    [classes.licenseplate]: true,
    [mappedCountry || 'neutral']: true,
    [size || '']: true,
    [registrationDisplay.variantClassName || '']: true,
    square: isSquare,
    'without-box': !isBlueBox
  })
  const countryClassName = classNames('country', {
    eu: isBlueBox && isEU,
    'no-blue-box': !isBlueBox
  })
  const rows = isSquare ? 2 : 1
  let maskedValue = value
  if (isSquare) {
    // replace first dash with linebreak for square plates
    maskedValue = value.replace('-', '\u000A')
  }

  return (
    <div className={required ? classes.root : undefined} data-testid='license-plate-base'>
      <div className={baseClassNames}>
        <div className={countryClassName}>
          <span>{registrationDisplay.countryCodeText}</span>
        </div>
        <Box className='number'>
          <div className={classes.hiddenSpacer} data-testid='license-plate-hidden-spacer'>
            {value}
          </div>
          {isFunction(children)
            ? children(classes, rows, maskedValue)
            : children || <span style={{ whiteSpace: 'pre-line' }}>{maskedValue}</span>}
        </Box>
      </div>
      {required && <Box sx={{ ml: 1, color: !disabled ? 'error.main' : 'action.disabled' }}>*</Box>}
    </div>
  )
}
